import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SupplierRequestFullType } from '@econtractor/types';
import * as apiSupplierRequests from 'api/apiSupplierRequests';
import { useToast } from 'context/ToastContext';
import { safeFloatParse, safeIntParse } from 'utils/helpers/safeParsing';
import { SUPPLIER_REQUEST_STATUS } from './constants';
import type { SupplierRequestDraftItem } from './types';

interface UseViewSupplierRequestParams {
    requestId: number | null;
    onClose: () => void;
}

const useViewSupplierRequest = ({ requestId, onClose }: UseViewSupplierRequestParams) => {
    const { t } = useTranslation(['Warehouses', 'common']);
    const { showToast } = useToast();
    const [request, setRequest] = useState<SupplierRequestFullType | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const id = safeIntParse(requestId);
        if (!id) {
            setRequest(null);
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setRequest(null);
        setIsLoading(true);

        void (async () => {
            const res = await apiSupplierRequests.getById(id);
            if (cancelled) return;
            setIsLoading(false);
            if (!res) {
                showToast(t('common:messError'), 'danger');
                onClose();
                return;
            }
            setRequest(res);
        })();

        return () => {
            cancelled = true;
        };
        // Only when requestId changes — not when toast/i18n identities change after mutations
    }, [requestId]);

    const items: SupplierRequestDraftItem[] = useMemo(
        () =>
            (request?.items || []).map((item) => ({
                productId: safeIntParse(item.productId) || 0,
                prodName: String(item.prodName || ''),
                prodArticle: String(item.prodArticle || ''),
                itemCount: safeFloatParse(item.itemCount) || 0,
                receivedCount: safeFloatParse(item.receivedCount) || 0,
                prodDefPrice: safeFloatParse(item.prodDefPrice) || 0,
                prodDefPriceSale: safeFloatParse(item.prodDefPriceSale) || 0,
                vendorName: String(item.vendorName || request?.vendorName || ''),
            })),
        [request],
    );

    const hasRemaining = useMemo(
        () =>
            items.some(
                (item) =>
                    (safeFloatParse(item.itemCount) || 0) -
                        (safeFloatParse(item.receivedCount) || 0) >
                    1e-9,
            ),
        [items],
    );

    const isActive = Number(request?.status) === SUPPLIER_REQUEST_STATUS.ACTIVE;

    return {
        t,
        request,
        setRequest,
        items,
        isLoading,
        isActive,
        hasRemaining,
        handleHide: onClose,
    };
};

export default useViewSupplierRequest;
